import { useEffect, useRef, useState, useCallback } from 'react';

interface Options {
  immediate?: boolean;
}


export const  useInterval = (callback: () => void, delay: number | null, options: Options = {}) => {
  const savedCallback = useRef<() => void>(() => {});

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    if (options.immediate) {
      savedCallback.current();
    }

    function tick() {
      savedCallback.current();
    }

    if (delay !== null) {
      const id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
    // 添加一个默认返回值，如果delay为null，则返回一个空函数
    return () => {};
  }, [delay, options.immediate]);
};


type Fn = (...args: any[]) => any;

interface Options {
  wait?: number;
  leading?: boolean;
  trailing?: boolean;
}

interface Result<T extends Fn> {
  run: (...args: Parameters<T>) => void;
  cancel: () => void;
  flush: () => void;
  value: ReturnType<T> | undefined;
}

export function useThrottleFn<T extends Fn>(fn: T, options: Options = {}): Result<T> {
  const { wait = 1000, leading = false, trailing = true } = options;
  const fnRef = useRef<T>(fn);
  const argsRef = useRef<any[]>([]);
  const [result, setResult] = useState<ReturnType<T>>();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const previousRef = useRef<number>(0);
  
  // 更新最新的函数引用
  useEffect(() => {
    fnRef.current = fn;
  }, [fn]);
  
  const handleFn = useCallback(() => {
    const now = Date.now();
    const hasElapsed = now - previousRef.current >= wait;
    
    if (hasElapsed) {
      setResult(fnRef.current(...argsRef.current));
      previousRef.current = now;
      timeoutRef.current = null;
    } else if (trailing && timeoutRef.current === null) {
      const remaining = wait - (now - previousRef.current);
      timeoutRef.current = setTimeout(() => {
        setResult(fnRef.current(...argsRef.current));
        previousRef.current = Date.now();
        timeoutRef.current = null;
      }, remaining);
    }
  }, [wait, trailing]);
  
  const run = useCallback((...args: Parameters<T>) => {
    argsRef.current = args;
    const now = Date.now();
    
    if (timeoutRef.current === null && leading && (now - previousRef.current >= wait)) {
      setResult(fnRef.current(...args));
      previousRef.current = now;
    } else {
      handleFn();
    }
  }, [handleFn, leading, wait]);
  
  const cancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);
  
  const flush = useCallback(() => {
    cancel();
    setResult(fnRef.current(...argsRef.current));
    previousRef.current = Date.now();
  }, [cancel]);
  
  useEffect(() => {
    return () => {
      cancel();
    };
  }, [cancel]);
  
  return {
    run,
    cancel,
    flush,
    value: result,
  };
}
